import React, { useRef, useState } from 'react';
import { formatFileSize } from '../lib/format';

function getFileBadge(file) {
  const name = file.name.toLowerCase();

  if (name.endsWith('.pdf')) {
    return 'PDF';
  }

  if (/\.(jpg|jpeg|png|webp)$/i.test(name)) {
    return 'IMG';
  }

  if (/\.docx$/i.test(name)) {
    return 'DOCX';
  }

  return 'FILE';
}

export default function FileDropzone({
  files,
  disabled,
  progress,
  onFilesAdd,
  onFileRemove,
}) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);

    if (disabled) {
      return;
    }

    const droppedFiles = Array.from(event.dataTransfer.files || []);
    onFilesAdd(droppedFiles);
  };

  const handleInputChange = (event) => {
    const nextFiles = Array.from(event.target.files || []);
    onFilesAdd(nextFiles);
    event.target.value = '';
  };

  return (
    <div className="file-dropzone-wrap">
      <button
        type="button"
        className={`file-dropzone ${isDragging ? 'dragging' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(event) => {
          event.preventDefault();
          if (!disabled) {
            setIsDragging(true);
          }
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          if (event.currentTarget.contains(event.relatedTarget)) {
            return;
          }
          setIsDragging(false);
        }}
        onDragOver={(event) => {
          event.preventDefault();
        }}
        onDrop={handleDrop}
        disabled={disabled}
        data-cursor-interactive="true"
      >
        <span className="file-dropzone-badge">UPLOAD</span>
        <strong>Drag and drop your files</strong>
        <span>or click to browse PDFs, DOCX files, and images.</span>
      </button>

      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        multiple
        accept=".pdf,.docx,.jpg,.jpeg,.png,.webp"
        onChange={handleInputChange}
        disabled={disabled}
      />

      {files.length > 0 ? (
        <div className="file-list">
          {files.map((file, index) => (
            <div className="file-item" key={`${file.name}-${file.size}-${index}`}>
              <span className="file-item-badge">{getFileBadge(file)}</span>
              <div className="file-item-copy">
                <strong>{file.name}</strong>
                <span>
                  {formatFileSize(file.size)}
                  {typeof file.pageCount === 'number'
                    ? ` | ${file.pageCount} page${file.pageCount !== 1 ? 's' : ''}`
                    : ''}
                </span>
              </div>
              <button
                type="button"
                className="file-remove-btn"
                onClick={() => onFileRemove(index)}
                disabled={disabled}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {progress?.visible ? (
        <div className="upload-progress">
          <div className="upload-progress-copy">
            <span>
              Uploading {progress.fileIndex}/{progress.totalFiles}
            </span>
            <strong>{progress.currentFile}</strong>
          </div>
          <div className="upload-progress-track">
            <span
              className="upload-progress-bar"
              style={{ width: `${progress.overallProgress || 0}%` }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
